// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';

import { ID_SCROLL_TAB_BLOCK } from "../../../../constants/uiConfigs";

import alert from "../../../../share/alert";
import config from "../../../../share/config";
import treeView from "../../../../share/components/vue/TreeView";
import { createAllDtcTreeData, createIndividualTreeData, clickObdAllDtcTreeNode,
  clickObdIndividualTreeNode, checkRouteChangeAndSelectNode, setTreeDataList } from "../../../../util/obd/tree";
import { EXECUTE_FUNCTION_TYPE_OBD_ALL_DTC, EXECUTE_FUNCTION_TYPE_OBD_INDIVIDUAL } from "../../../../util/obd/functions";
import { PAGE_FROM_START } from "../../../../router/obd";

export default {
  name: "obdBase",
  data() {
    return {
      isTreeViewOpen: false,
      isTreeViewShow: true,
      treeList: [],
      isLangRu: false,
    };
  },

  beforeRouteLeave(to, from, next) {
    if (to.name === "home") {
      // OBDBase の子コンポーネントでは確認ダイアログ表示の実装は不要
      const messages = this.$t("message.message_vehicle_dtc_confirm_back_home");
      alert.showConfirmation(this, null, messages, () => {
        // OKボタンが押下された場合はstart画面に戻す
        next();
      });
    } else {
      next();
    }
  },

  watch: {
    viewMode: {
      immediate: true,
      handler(val) {
        this.isTreeViewOpen = this.isTreeViewOpen ? val === config.PROCESS_IDLING : false;
        this.isTreeViewShow = val === config.PROCESS_IDLING;
      },
    },

    language: {
      immediate: true,
      handler(val) {
        this.updateTreeView(this.$route.name);
        // ロシア語の場合、文字サイズを小さくする
        this.isLangRu = val === "ru"? true : false;
      },
    },

    obdMenuSupport: {
      handler() {
        this.updateTreeView(this.$route.name);
      },
    },

    $route: {
      immediate: true,
      handler: function (to) {
        this.updateTreeView(to.name);
      },
    },
  },

  components: {
    "system-tree": treeView,
  },

  methods: {
    ...mapActions('cache', ['cacheIsTreeViewOpen']),

    /**
     * ツリービューの表示・非表示を切り替える。
     */
    switchTreeDisplay() {
      this.isTreeViewOpen = !this.isTreeViewOpen;
    },


    /**
     * ツリービューの更新を行う。
     * @param {string} routeName ルートの名前
     */
    updateTreeView(routeName) {
      const treeList = [];

      // 全DTCのツリー定義
      setTreeDataList(
        this,
        treeList,
        EXECUTE_FUNCTION_TYPE_OBD_ALL_DTC,
        createAllDtcTreeData(this),
        null, // judgeVisible
        () => {
          // 全DTCツリーノードクリック処理
          const clickObdAllDtcLogic = () => {
            // ツリーを開き状態にする
            this.cacheIsTreeViewOpen(true);
            clickObdAllDtcTreeNode(this, PAGE_FROM_START);
          };

          // OBDシステム情報が存在しない場合、確認ダイアログを表示
          if (!this.obdSystems || !this.obdSystems.length) {
            // 選択していたノードを再選択する
            const routeName = this.$router.currentRoute.value.name;
            checkRouteChangeAndSelectNode(
              this,
              this.filteredTreeList,
              this.$refs.trees,
              routeName
            );
            // 確認ダイアログ
            alert.showConfirmation(
              this,
              null,
              this.$t('fixed_resource.label_confirm_start_connection_obd_vehicle'),
              // 続行時のコールバック
              () => {
                // 全DTCツリーノードクリック処理
                clickObdAllDtcLogic();
              }
            );
          } else {
            // 個全DTCツリーノードクリック処理
            clickObdAllDtcLogic();
          }
        },
        null,
        `${this.$t("fixed_resource.obd_menu_alldtc")}${this.$t(
          "fixed_resource.obd_menu"
        )}`
      );

      // 個別システム診断のツリー定義
      setTreeDataList(
        this,
        treeList,
        EXECUTE_FUNCTION_TYPE_OBD_INDIVIDUAL,
        createIndividualTreeData(
          this,
          this.obdMenuSupport,
          this.obdSystems
        ),
        null, // judgeVisible
        (node) => {
          // 個別診断ツリーノードクリック処理
          const clickObdIndividualLogic = () => {
            // ツリーを開き状態にする
            this.cacheIsTreeViewOpen(true);
            clickObdIndividualTreeNode(this, PAGE_FROM_START, node);
          };

          // OBDシステム情報が存在しない場合、確認ダイアログを表示
          if (!this.obdSystems || !this.obdSystems.length) {
            // 選択していたノードを再選択する
            const routeName = this.$router.currentRoute.value.name;
            checkRouteChangeAndSelectNode(
              this,
              this.filteredTreeList,
              this.$refs.trees,
              routeName
            );
            // 確認ダイアログ
            alert.showConfirmation(
              this,
              null,
              this.$t('fixed_resource.label_confirm_start_connection_obd_vehicle'),
              // 続行時のコールバック
              () => {
                // 個別診断ツリーノードクリック処理
                clickObdIndividualLogic();
              }
            );
          } else {
            // 個別診断ツリーノードクリック処理
            clickObdIndividualLogic();
          }
        },
        null,
        `${this.$t("fixed_resource.obd_menu_individual")}${this.$t(
          "fixed_resource.obd_menu"
        )}`
      );

      // OBD に遷移した場合のツリー選択・非選択処理
      checkRouteChangeAndSelectNode(
        this,
        this.filteredTreeList,
        this.$refs.trees,
        routeName
      );

      this.treeList = treeList;
    },
  },

  computed: {
    ...mapState({
      viewMode: (state) => state.app.viewProcessMode,
      language: (state) => state.systems.language,
      obdMenuSupport: (state) => state.obd.menuSupport,
      obdSystems: (state) => state.obd.systems,
      isCacheTreeViewOpen: (state) => state.cache.isTreeViewOpen,
    }),
    filteredTreeList() {
      return this.treeList.filter((tree) => tree.judgeVisible());
    },

    /**
     * 画面上部判定用 HTML の ID を取得する。
     * @returns {string} ID
     */
    idScrollTabBlock: () => ID_SCROLL_TAB_BLOCK,
  },

  mounted() {
    this.updateTreeView(this.$route.name);

    // ツリー押下時、(1)ツリーがない画面 (InformationMain 以外の画面) を経由して、(2)ツリーがある画面にリダイレクトさせるケースがある。
    // (2)のタイミングで isTreeViewOpen は初期化されてしまうので、キャッシュするようにする。
    // 誤動作防止のため、キャッシュの復元直後はクリアする。
    this.isTreeViewOpen = this.isCacheTreeViewOpen? this.isCacheTreeViewOpen : false;
    // キャッシュ（isCacheTreeViewOpen）が存在すればクリアする
    if (this.isCacheTreeViewOpen) {
      this.cacheIsTreeViewOpen(false);
    }
  },
};
